
import React from 'react';
import { ShieldCheck, Globe, FileCheck, BookOpen, MessageSquare } from 'lucide-react';
import { ServiceDetail, BlogPost, User, Testimonial } from './types';

export const INITIAL_USERS: User[] = [
  {
    id: 'admin-1',
    name: 'CA Manthan Paliwal',
    email: 'manthan@ca.com',
    password: 'admin123',
    isAdmin: true,
    role: 'admin',
    designation: 'FCA, Strategic Tax & Compliance Architect',
    bio: 'Deploying high-resolution statutory architecture and ICFR frameworks for multi-national conglomerates bridging the India-UAE corridor.',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=800&auto=format&fit=crop',
    caLogo: 'https://upload.wikimedia.org/wikipedia/commons/b/b9/CA_Logo_India.png',
    specializations: ['Statutory Audit & Assurance', 'Ind-AS Conversion', 'UAE Corporate Tax Architecture'],
    linkedinUrl: 'https://linkedin.com/in/manthanpaliwal',
    location: 'Mumbai | Dubai',
    yearsExperience: 12,
    education: ['Fellow Member of ICAI (FCA)', 'Certified IFRS Professional'],
    achievements: ['Optimized ICFR for 50+ Global Entities', 'Cross-Border M&A Tax Due Diligence Lead'],
    philosophyTitle: 'The Doctrine of Fiscal Integrity',
    philosophyText: 'Accounting is not merely record-keeping; it is the structural preservation of fiduciary trust. We build frameworks that are audit-proof by design, ensuring absolute statutory resilience.'
  }
];

export const INITIAL_TESTIMONIALS: Testimonial[] = [
  {
    id: 'test-1',
    name: "Rajesh Sharma",
    role: "CFO, TechVantage India",
    content: "CA Manthan's deployment of ICFR protocols significantly hardened our control environment. His audit-readiness strategy is exemplary.",
  },
  {
    id: 'test-2',
    name: "Sarah Al-Maktoum",
    role: "Managing Director, Dubai Fin-Tech",
    content: "The UAE Corporate Tax transition was seamless thanks to Manthan's statutory screening. He provided the technical clarity our board required.",
  }
];

export const SERVICES: ServiceDetail[] = [
  {
    id: 'indian-statutory',
    title: 'Statutory Audit & Domestic Compliance',
    icon: 'ShieldCheck',
    shortDesc: 'Hardened regulatory infrastructure and statutory stewardship for Indian entities requiring zero-error fiscal precision.',
    longDesc: 'Our domestic compliance engine is engineered for statutory absolute. We deploy comprehensive stewardship modules covering GST, Income Tax, and ROC, focusing on ICFR integrity and audit trail preservation.',
    scope: [
      'Statutory & Tax Audit Preparation',
      'Ind-AS & GAAP Conversion Services',
      'Internal Controls (ICFR) Hardening',
      'GST Lifecycle & ITC Optimization',
      'Continuous Audit Readiness Protocols'
    ],
    methodology: [
      { title: 'Fiscal Due Diligence', description: 'Deep-dive forensic analysis of transaction cycles and tax touchpoints.' },
      { title: 'Control Framework', description: 'Deploying an audit-proof Chart of Accounts aligned with Ind-AS.' },
      { title: 'Systemic Monitoring', description: 'Daily verification of ledger entries to eliminate compliance leakage.' },
      { title: 'Fiduciary Reporting', description: 'Strategic P&L insights and board-ready MIS packages.' }
    ],
    focus: 'Absolute statutory hygiene and audit trail preservation.',
    benefits: [
      'Mitigation of Tax Assessment Risks',
      'Enhanced ICFR Maturity Scores',
      'Optimized Working Capital via Data Analytics'
    ],
    tools: ['SAP S/4HANA Compliance Layers', 'Tally Prime Elite', 'Zoho Books Enterprise'],
    faqs: [
      { question: 'Do you manage internal audits?', answer: 'We architect the framework so that external and internal audits become seamless verification cycles rather than correction exercises.' }
    ]
  },
  {
    id: 'foreign-ifrs',
    title: 'IFRS & Multi-Jurisdictional Reporting',
    icon: 'Globe',
    shortDesc: 'IFRS-aligned global reporting modules and multi-currency stewardship for high-growth cross-border entities.',
    longDesc: 'Strategic financial operations for global firms. We serve as your decentralized Controllership, bridging jurisdictional gaps with IFRS-compliant reporting and multi-currency reconciliation.',
    scope: [
      'IFRS & US-GAAP Reporting Packages',
      'Multi-Currency Consolidation Protocols',
      'Accounts Payable & Treasury Stewardship',
      'Global Tax Liaison & Nexus Analysis',
      'SaaS Burn & Runway Telemetry'
    ],
    methodology: [
      { title: 'Jurisdictional Logic', description: 'Mapping operational data to international IFRS/GAAP standards.' },
      { title: 'Cloud Stack Integration', description: 'Deploying automated ledger sync using Xero or QBO Advanced.' },
      { title: 'Real-time Assurance', description: '24/7 telemetry into global cash-burn and runway metrics.' },
      { title: 'Fiduciary Consolidation', description: 'Aggregating multi-entity data into unified board reporting.' }
    ],
    focus: 'Institutional-grade reliability for global scale.',
    benefits: [
      'Standardized Global Reporting Frameworks',
      'Automated Multi-Currency Forex Monitoring',
      'Reduced Operational Overhead for Offshore Hubs'
    ],
    tools: ['Xero Advisor Tier', 'QBO Advanced', 'Dext Precision', 'Ramp Integrations'],
    faqs: [
      { question: 'How do you handle time-zone latency?', answer: 'Our Controllership team operates on overlapping shifts to ensure real-time coordination with UK, US, and UAE stakeholders.' }
    ]
  },
  {
    id: 'uae-tax',
    title: 'UAE Corporate Tax & AML Hardening',
    icon: 'FileCheck',
    shortDesc: 'Process-driven UAE Corporate Tax readiness, AML screening, and ESR compliance orchestration.',
    longDesc: 'Advanced regulatory screening designed to streamline entity verification and document hygiene within the Emirates. We future-proof UAE businesses against the maturing tax landscape.',
    scope: [
      'Corporate Tax Registration & Strategy',
      'AML/KYC Compliance Orchestration',
      'Economic Substance (ESR) Readiness',
      'Free Zone Substance Verification',
      'Ultimate Beneficial Owner (UBO) Filing'
    ],
    methodology: [
      { title: 'Perimeter Mapping', description: 'Identifying jurisdictional vulnerabilities in UAE Mainland/Free Zones.' },
      { title: 'Forensic Document Review', description: 'Verification of corporate records against FTA standards.' },
      { title: 'Regulatory Hardening', description: 'Implementing SOPs for counterparty onboarding and AML.' },
      { title: 'Compliance Audits', description: 'Quarterly sanity checks on tax and substance status.' }
    ],
    focus: 'Mitigating regulatory friction in the UAE corridor.',
    benefits: [
      'Accelerated Institutional Banking Onboarding',
      'Seamless Corporate Tax Transitioning',
      'Zero-Penalty Compliance Status'
    ],
    tools: ['FTA Compliance Frameworks', 'Internal AML Telemetry'],
    faqs: [
      { question: 'Is this a legal service?', answer: 'We focus on the technical and operational processing of compliance data, ensuring your entity is audit-ready for legal and banking reviews.' }
    ]
  }
];

export const BLOG_POSTS: BlogPost[] = [
  {
    id: 'm-1',
    title: 'The Doctrine of Substance: Navigating UAE Corporate Tax 2025',
    imageUrl: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=80&w=2000&auto=format&fit=crop',
    category: 'UAE Compliance',
    date: 'Feb 24, 2025',
    readingTime: '9 min read',
    author: 'CA Manthan Paliwal',
    authorId: 'admin-1',
    excerpt: 'An institutional analysis of substance requirements and qualifying income under the new UAE 9% regime.',
    views: 3420,
    likes: 128,
    shares: 56,
    commentCount: 18,
    status: 'published',
    content: `The introduction of UAE Corporate Tax represents a paradigm shift from a tax-free haven to a regulated global financial hub. Understanding the nuances of "Qualifying Income" and "Substance-Over-Form" is now mandatory for fiduciary survival.`
  }
];

export const ETHICS = [
  { title: 'Professional Skepticism', desc: 'Continuous critical assessment of data to ensure absolute fiscal honesty.' },
  { title: 'Fiduciary Neutrality', desc: 'Maintaining objective professional distance in all assurance engagements.' },
  { title: 'Institutional Secrecy', desc: 'Enterprise-grade protocols for the protection of sensitive financial logic.' }
];
